/**
 * Preparação das imagens antes de capturar o documento do orçamento como PDF.
 *
 * Contexto do problema que este módulo resolve: a assinatura e a logo NÃO são
 * data URLs quando o PDF é gerado. A etapa 3 sobe a assinatura para o Blob
 * Storage e grava a URL (ver SalvarEtapa3 na API); a logo segue o mesmo caminho.
 * O html-to-image só embute imagens que já estejam carregadas e decodificadas —
 * uma <img> que ainda está baixando é capturada como espaço em branco, sem erro.
 *
 * A implementação anterior convertia para base64 e esperava 100 ms fixos. Em
 * máquina mais lenta ou com imagem grande, a decodificação não terminava nesse
 * prazo e a imagem sumia do PDF — daí a falha ser intermitente e depender de
 * navegador e sistema.
 *
 * Aqui cada imagem é convertida, redesenhada em canvas no tamanho de exibição e
 * só então considerada pronta. O canvas resolve dois problemas de uma vez:
 * desenhar exige que a imagem esteja realmente decodificada, e o resultado sai
 * pequeno — uma logo de 2 MB viraria um data URL de ~2,7 MB dentro do SVG que o
 * html-to-image monta, o que falha silenciosamente em alguns navegadores.
 */

/** Resolução extra sobre o tamanho de exibição, para a imagem não sair borrada. */
const FATOR_NITIDEZ = 3;

/** Teto de segurança por imagem, para não gerar um data URL gigante. */
const LADO_MAXIMO_PX = 1200;

export interface ResultadoPreparacaoImagens {
  /** Descrição das imagens que não puderam ser preparadas (alt ou URL). */
  falhas: string[];
}

/** Converte uma URL remota em data URL. Requer CORS liberado na origem. */
async function urlParaDataUrl(url: string): Promise<string> {
  const resposta = await fetch(url, { mode: "cors", cache: "no-cache" });
  if (!resposta.ok) {
    throw new Error(`HTTP ${resposta.status} ao baixar ${url}`);
  }

  const blob = await resposta.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Falha ao ler a imagem baixada"));
    reader.readAsDataURL(blob);
  });
}

/** Carrega um data URL num HTMLImageElement novo, já decodificado. */
function carregarImagem(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.onload = () => resolve(imagem);
    imagem.onerror = () => reject(new Error("Imagem não pôde ser carregada"));
    imagem.src = src;
  });
}

/**
 * Espera a <img> do próprio documento terminar de decodificar o src atual.
 * É isto que o html-to-image vai clonar, então é isto que precisa estar pronto —
 * não uma cópia em memória.
 */
async function aguardarDecodificacao(img: HTMLImageElement): Promise<void> {
  if (typeof img.decode === "function") {
    try {
      await img.decode();
      return;
    } catch {
      // Safari antigo rejeita decode() mesmo com imagem válida; cai no onload.
    }
  }

  if (img.complete && img.naturalWidth > 0) return;

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Imagem não pôde ser carregada"));
  });
}

/**
 * Redesenha a imagem em canvas no tamanho em que ela aparece na tela.
 * Devolve um PNG data URL leve e comprovadamente decodificado.
 */
function normalizarViaCanvas(
  imagem: HTMLImageElement,
  larguraExibida: number,
  alturaExibida: number,
): string {
  const proporcao = imagem.naturalWidth / imagem.naturalHeight || 1;

  // Sem caixa medida (imagem oculta no momento), cai para o tamanho natural.
  let largura = larguraExibida > 0 ? larguraExibida * FATOR_NITIDEZ : imagem.naturalWidth;
  let altura = alturaExibida > 0 ? alturaExibida * FATOR_NITIDEZ : imagem.naturalHeight;

  // Nunca ampliar além do original: só gera peso, não ganha detalhe.
  largura = Math.min(largura, imagem.naturalWidth || largura);
  altura = Math.min(altura, imagem.naturalHeight || altura);

  // Teto absoluto, preservando a proporção.
  if (largura > LADO_MAXIMO_PX) {
    largura = LADO_MAXIMO_PX;
    altura = largura / proporcao;
  }
  if (altura > LADO_MAXIMO_PX) {
    altura = LADO_MAXIMO_PX;
    largura = altura * proporcao;
  }

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(largura));
  canvas.height = Math.max(1, Math.round(altura));

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas indisponível neste navegador");

  // Fundo branco: assinatura em PNG transparente ficaria invisível caso o
  // html-to-image renderize sobre fundo escuro.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(imagem, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL("image/png");
}

/**
 * Converte todas as <img> do elemento em data URLs prontos para captura.
 *
 * Não lança: devolve a lista de falhas para o chamador decidir. Um PDF de
 * orçamento sem a assinatura não pode ser enviado em silêncio.
 */
export async function prepararImagensParaPdf(
  element: HTMLElement,
): Promise<ResultadoPreparacaoImagens> {
  const falhas: string[] = [];
  const imagens = Array.from(element.querySelectorAll("img"));

  await Promise.all(
    imagens.map(async (img) => {
      const origem = img.getAttribute("src") ?? "";
      if (!origem) return;

      // Medir antes de trocar o src: a caixa CSS não muda, mas evita ler
      // dimensões durante um reflow provocado pela própria troca.
      const caixa = img.getBoundingClientRect();

      try {
        const dataUrl = origem.startsWith("data:")
          ? origem
          : await urlParaDataUrl(origem);

        const carregada = await carregarImagem(dataUrl);
        const normalizada = normalizarViaCanvas(
          carregada,
          caixa.width,
          caixa.height,
        );

        img.src = normalizada;

        // Esperar a <img> do documento adotar o novo src, e não um prazo fixo.
        await aguardarDecodificacao(img);
      } catch (erro) {
        console.error("Falha ao preparar imagem para o PDF:", origem, erro);
        falhas.push(img.alt || origem);
      }
    }),
  );

  return { falhas };
}
